/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListToolsetToolFiltersRequest,
  ListToolsetToolFiltersSecurity,
} from "../models/operations/listtoolsettoolfilters.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildListToolsetToolFiltersQuery,
  ListToolsetToolFiltersQueryData,
  prefetchListToolsetToolFilters,
  queryKeyListToolsetToolFilters,
} from "./listToolsetToolFilters.core.js";
export {
  buildListToolsetToolFiltersQuery,
  type ListToolsetToolFiltersQueryData,
  prefetchListToolsetToolFilters,
  queryKeyListToolsetToolFilters,
};

export type ListToolsetToolFiltersQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listToolFilters toolsets
 *
 * @remarks
 * List the tool filter scopes (tags) available on a toolset-backed MCP server and the tools under each, including tools excluded from all filters. Read-only; reflects the explicit tool variations group configured on the toolset, deriving effective tags with the same logic as the runtime ?tags= filter. Returns filtering disabled when no explicit group is set.
 */
export function useListToolsetToolFilters(
  request: ListToolsetToolFiltersRequest,
  security?: ListToolsetToolFiltersSecurity | undefined,
  options?: QueryHookOptions<
    ListToolsetToolFiltersQueryData,
    ListToolsetToolFiltersQueryError
  >,
): UseQueryResult<
  ListToolsetToolFiltersQueryData,
  ListToolsetToolFiltersQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildListToolsetToolFiltersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listToolFilters toolsets
 *
 * @remarks
 * List the tool filter scopes (tags) available on a toolset-backed MCP server and the tools under each, including tools excluded from all filters. Read-only; reflects the explicit tool variations group configured on the toolset, deriving effective tags with the same logic as the runtime ?tags= filter. Returns filtering disabled when no explicit group is set.
 */
export function useListToolsetToolFiltersSuspense(
  request: ListToolsetToolFiltersRequest,
  security?: ListToolsetToolFiltersSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    ListToolsetToolFiltersQueryData,
    ListToolsetToolFiltersQueryError
  >,
): UseSuspenseQueryResult<
  ListToolsetToolFiltersQueryData,
  ListToolsetToolFiltersQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildListToolsetToolFiltersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setListToolsetToolFiltersData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      slug: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: ListToolsetToolFiltersQueryData,
): ListToolsetToolFiltersQueryData | undefined {
  const key = queryKeyListToolsetToolFilters(...queryKeyBase);

  return client.setQueryData<ListToolsetToolFiltersQueryData>(key, data);
}

export function invalidateListToolsetToolFilters(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      slug: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "toolsets", "listToolFilters", ...queryKeyBase],
  });
}

export function invalidateAllListToolsetToolFilters(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "toolsets", "listToolFilters"],
  });
}
